import React from 'react';
import './privacyPolicy.css';

const PrivacyPolicy = () => {
    return (
        <div className="container privacy-page-container">
            <div className="privacy-content-wrapper">
                <div className="privacy-header">
                    <h1 className="main-title">Privacy Policy</h1>
                    <span className="last-updated">Last Updated: December 30, 2025</span>
                    <p style={{ marginTop: '20px', color: 'var(--steel-blue-color)' }}>
                        This Privacy Policy explains how <strong>Protradeplus Inc</strong> ("we", "us", or "our") collects, uses, discloses, and safeguards personal information when you use <strong>Protrade+</strong> (the "App"), available on the <strong>Apple App Store</strong> and <strong>Google Play Store</strong>.
                    </p>
                    <p style={{ color: 'var(--steel-blue-color)' }}>
                        By downloading or using the App, you agree to the practices described in this Privacy Policy.
                    </p>
                </div>

                <div className="policy-section">
                    <h2>1. Information We Collect</h2>
                    <p>We collect the following categories of personal information.</p>

                    <h3>1.1 Personal Identifiers</h3>
                    <ul>
                        <li>Full name</li>
                        <li>Email address</li>
                        <li>Phone number</li>
                        <li>Physical mailing address</li>
                        <li>Login provider identifiers (such as Apple ID, Google account ID, or other third-party authentication identifiers)</li>
                    </ul>

                    <h3>1.2 Device & Network Information</h3>
                    <ul>
                        <li>IP address and network port</li>
                        <li>Mobile carrier</li>
                        <li>Device type, operating system, and app version</li>
                    </ul>
                    <p>Information is collected directly from you.</p>
                </div>

                <div className="policy-section">
                    <h2>2. How We Use Your Information</h2>
                    <p>We use personal information for the following purposes:</p>
                    <ul>
                        <li>Creating and managing user accounts</li>
                        <li>Authenticating users via login providers</li>
                        <li>Providing core App functionality</li>
                        <li>Communicating with users regarding accounts, security, or service updates</li>
                        <li>Fraud prevention and security monitoring</li>
                        <li>Maintaining and improving App performance and reliability</li>
                        <li>Complying with legal and regulatory obligations</li>
                    </ul>
                    <p>We do <strong>not</strong> use personal information for purposes unrelated to the App without consent.</p>
                </div>

                <div className="policy-section">
                    <h2>3. Legal Basis for Processing</h2>
                    <h3>3.1 Canada</h3>
                    <p>We collect, use, and disclose personal information in accordance with the <strong>Personal Information Protection and Electronic Documents Act (PIPEDA)</strong> and applicable provincial privacy laws. Consent may be express or implied, depending on the sensitivity of the information and context.</p>

                    <h3>3.2 GDPR (EU / EEA / UK)</h3>
                    <p>Where the GDPR applies, we process personal data based on:</p>
                    <ul>
                        <li><strong>Contractual necessity</strong> (providing the App and account services)</li>
                        <li><strong>Legitimate interests</strong> (security, fraud prevention, service improvement)</li>
                        <li><strong>Consent</strong>, where required</li>
                        <li><strong>Legal obligations</strong></li>
                    </ul>
                    <p>You may withdraw consent at any time.</p>
                </div>

                <div className="policy-section">
                    <h2>4. Sharing of Personal Information</h2>
                    <p>We do <strong>not sell</strong> personal information.</p>
                    <p>We may share personal information with:</p>
                    <ul>
                        <li>Login and authentication providers (e.g., Apple, Google)</li>
                        <li>Hosting, infrastructure, and security service providers</li>
                        <li>Customer support or operational service providers</li>
                        <li>Government authorities or law enforcement when required by law</li>
                    </ul>
                    <p>All third parties are required to protect personal information and use it only for authorized purposes.</p>
                </div>

                <div className="policy-section">
                    <h2>5. Analytics & Diagnostics</h2>
                    <p>We may collect limited technical and diagnostic information to:</p>
                    <ul>
                        <li>Monitor App performance</li>
                        <li>Identify and fix errors</li>
                        <li>Improve reliability and functionality</li>
                    </ul>
                    <p>We do not use analytics data for advertising or cross-app tracking without consent.</p>
                </div>

                <div className="policy-section">
                    <h2>6. Advertising & Tracking</h2>
                    <ul>
                        <li>The App does <strong>not</strong> engage in cross-app or cross-website tracking without explicit user consent.</li>
                        <li>On iOS, tracking permissions are governed by <strong>App Tracking Transparency (ATT)</strong>.</li>
                        <li>On Android, advertising preferences can be managed through device settings.</li>
                    </ul>
                </div>

                <div className="policy-section">
                    <h2>7. Data Retention</h2>
                    <p>We retain personal information only as long as necessary to:</p>
                    <ul>
                        <li>Maintain user accounts</li>
                        <li>Provide App services</li>
                        <li>Fulfill legal, regulatory, or security requirements</li>
                    </ul>
                    <p>When information is no longer required, it is securely deleted or anonymized.</p>
                </div>

                <div className="policy-section">
                    <h2>8. Data Security</h2>
                    <p>We implement reasonable administrative, technical, and physical safeguards to protect personal information against loss, misuse, unauthorized access, disclosure, or alteration.</p>
                    <p>No method of transmission or storage is completely secure, and we cannot guarantee absolute security.</p>
                </div>

                <div className="policy-section">
                    <h2>9. Your Privacy Rights</h2>

                    <h3>9.1 Canada</h3>
                    <p>You have the right to:</p>
                    <ul>
                        <li>Access your personal information</li>
                        <li>Request correction of inaccurate or incomplete information</li>
                        <li>Withdraw consent, subject to legal or contractual limitations</li>
                    </ul>

                    <h3>9.2 GDPR (EU / EEA / UK)</h3>
                    <p>You have the right to:</p>
                    <ul>
                        <li>Access, correct, or delete your personal data</li>
                        <li>Restrict or object to processing</li>
                        <li>Request data portability</li>
                        <li>Withdraw consent at any time</li>
                        <li>Lodge a complaint with a data protection authority</li>
                    </ul>

                </div>

                <div className="policy-section">
                    <h3>9.3 CCPA / CPRA (California)</h3>
                    <p>California residents have the right to:</p>
                    <ul>
                        <li>Know what personal information is collected and used</li>
                        <li>Request deletion or correction of personal information</li>
                        <li>Opt out of sale or sharing (not applicable — we do not sell or share data for advertising)</li>
                        <li>Limit use of sensitive personal information (where applicable)</li>
                        <li>Not be discriminated against for exercising privacy rights</li>
                    </ul>
                    <p>We may need to verify your identity before fulfilling requests.</p>
                </div>

                <div className="policy-section">
                    <h2>10. International Data Transfers</h2>
                    <p>Your personal information may be stored or processed outside your country of residence, including in <strong>Canada</strong> and the <strong>United States</strong>.</p>
                    <p>Where required, we use appropriate safeguards such as contractual protections to ensure lawful data transfers.</p>
                </div>

                <div className="policy-section">
                    <h2>11. Children’s Privacy</h2>
                    <p>The App is not intended for children under the age of <strong>13</strong> (or the minimum age required by law in your jurisdiction).</p>
                    <p>We do not knowingly collect personal information from children.</p>
                </div>

                <div className="policy-section">
                    <h2>12. Changes to This Privacy Policy</h2>
                    <p>We may update this Privacy Policy from time to time.</p>
                    <p>Changes will be posted within the App or on our website, and the “Last updated” date will be revised.</p>
                </div>

                <div className="policy-section">
                    <h2>13. Contact Us</h2>
                    <p>If you have questions or wish to exercise your privacy rights, contact:</p>
                    <p>
                        <strong>Protradeplus Inc</strong><br />
                        Email: <a href="mailto:protradeplus2004@gmail.com" target='_blank'>protradeplus2004@gmail.com</a>
                    </p>
                </div>

            </div>
        </div>
    );
};

export default PrivacyPolicy;